import { useEffect, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useAppConfig } from '../../hooks/useAppConfig';
import { fetchCurriculumData } from '../../services/dataApi';
import { CurriculumProgressService } from '../../services/CurriculumProgressService';
import { LearningProgressService } from '../../services/LearningProgressService';

function normalizeLevel(value) {
  return String(value || '').trim().toLowerCase();
}

function DetailList({ title, items }) {
  if (!Array.isArray(items) || items.length === 0) return null;
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <h2 className="text-xs font-black uppercase tracking-widest text-indigo-600">{title}</h2>
      <ul className="mt-3 space-y-2 text-sm leading-relaxed text-slate-700">
        {items.map((item) => <li key={item} className="flex gap-2"><span className="text-indigo-500">✓</span><span>{item}</span></li>)}
      </ul>
    </section>
  );
}

export default function CurriculumDayPage() {
  const { level: rawLevel, day: rawDay } = useParams();
  const navigate = useNavigate();
  const { languagePrefs } = useAuth();
  const config = useAppConfig(languagePrefs?.nativeLanguage);
  const level = normalizeLevel(rawLevel);
  const dayNumber = Number(rawDay);
  const levels = Array.isArray(config.level) ? config.level.map(normalizeLevel).filter(Boolean) : [];
  const isAllowedLevel = levels.includes(level);
  const [curriculum, setCurriculum] = useState(null);
  const [error, setError] = useState(false);
  const langPair = languagePrefs
    ? `${languagePrefs.nativeLanguage}_${languagePrefs.targetLanguage}`
    : 'guest';

  useEffect(() => {
    if (!isAllowedLevel) return undefined;
    let active = true;
    fetchCurriculumData(languagePrefs?.targetLanguage, `${level}/curriculum.json`)
      .then((data) => { if (active) setCurriculum(data); })
      .catch(() => { if (active) setError(true); });
    return () => { active = false; };
  }, [isAllowedLevel, level, languagePrefs?.targetLanguage]);

  if (levels.length === 0) return <div className="page-shell"><div className="h-64 animate-pulse rounded-3xl bg-slate-100" /></div>;
  if (!isAllowedLevel || !Number.isInteger(dayNumber)) return <Navigate to={`/?level=${level}`} replace />;
  if (!curriculum && !error) return <div className="page-shell"><div className="h-64 animate-pulse rounded-3xl bg-slate-100" /></div>;
  if (error) return <Navigate to={`/?level=${level}`} replace />;

  const days = Array.isArray(curriculum.days) ? curriculum.days : [];
  const lesson = days.find((item) => item.day === dayNumber);
  const progress = CurriculumProgressService.snapshot(langPair, level, days.length);
  if (!lesson || !CurriculumProgressService.isUnlocked(dayNumber, progress)) {
    return <Navigate to={`/?level=${level}`} replace />;
  }

  const completed = progress.completedDays.includes(dayNumber);
  const nextDay = progress.nextDay;
  const completeLesson = () => {
    const updated = CurriculumProgressService.completeDay(langPair, level, dayNumber, days.length);
    LearningProgressService.record(langPair, 'basics', 1, {
      activityId: `curriculum:${level}:day:${dayNumber}`,
    });
    if (updated.nextDay) {
      navigate(`/curriculum/${level}/day/${updated.nextDay}`);
    } else {
      navigate(`/?level=${level}`);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-10 sm:px-6 lg:px-8">
      <main className="mx-auto max-w-4xl">
        <Link to={`/?level=${level}`} className="inline-flex items-center gap-2 text-sm font-bold text-indigo-700 transition hover:text-indigo-900"><span aria-hidden="true">←</span> Back to {level.toUpperCase()} ladder</Link>
        <section className="mt-6 overflow-hidden rounded-3xl bg-gradient-to-br from-indigo-700 via-violet-700 to-fuchsia-700 p-7 text-white shadow-xl sm:p-10">
          <p className="text-xs font-black uppercase tracking-[0.2em] text-white/70">{level.toUpperCase()} · Day {lesson.day} of {days.length}</p>
          <h1 className="mt-3 text-4xl font-black sm:text-5xl">{lesson.title}</h1>
          <p className="mt-3 text-sm font-bold text-white/80">{lesson.theme}</p>
          {lesson.objective && <p className="mt-5 max-w-2xl text-base leading-relaxed text-white/90">{lesson.objective}</p>}
        </section>

        <div className="mt-7 grid gap-4 md:grid-cols-2">
          <DetailList title="By the end of today" items={lesson.canDo} />
          <DetailList title="Vocabulary focus" items={lesson.vocabularyTopics} />
          <DetailList title="Grammar focus" items={lesson.grammarTopics} />
          <DetailList title="Skills to practise" items={lesson.skills} />
          <DetailList title="Pronunciation" items={lesson.pronunciationTopics} />
          <DetailList title="Communication goals" items={lesson.communicationFunctions} />
        </div>

        <section className="mt-7 rounded-3xl border border-indigo-200 bg-white p-6 text-center shadow-sm">
          <p className="text-sm font-semibold text-slate-600">{completed ? 'This day is complete. You can revisit it whenever you need.' : 'Finish this lesson when you are ready to unlock the next step.'}</p>
          <button
            type="button"
            onClick={completeLesson}
            className="mt-4 rounded-xl bg-gradient-to-r from-indigo-600 to-violet-600 px-6 py-3 text-sm font-black text-white shadow-md transition hover:-translate-y-0.5 hover:shadow-lg"
          >
            {completed && nextDay ? `Continue to Day ${nextDay}` : completed ? 'Return to your ladder' : dayNumber === days.length ? 'Finish the level' : 'Complete day and continue'}
          </button>
        </section>
      </main>
    </div>
  );
}
